import { z } from "zod";
import type { Locale } from "./common";
import type { PackageTheme } from "./flows";
import {
  treatmentPhaseSchema,
  type TreatmentPhase,
} from "./patient-journey-state";
import { homeTopSchema } from "./home-top";

// TES-311 [J-app] — Home v-next. The lightweight rollup that feeds three of
// the six Home sections that /me and /home-now don't already cover:
//   §1 Personlig orientering — greeting firstName + the weekly theme (veckotema)
//   §3 Veckoincheckning      — whether this ISO week's check-in is submitted
//   §5 "För dig idag"        — support copy on the SAME theme as the greeting
//
// Design rule (Canva spec, page 4): the greeting (§1) and "För dig idag" (§5)
// must always share ONE focus. We guarantee that structurally: both are
// derived from a single `veckotema` object, itself keyed off the DERIVED
// treatment phase. A candidate can win §2 ("Vad händer just nu") without
// replacing the theme — only medical safety pauses the theme, and that lives
// in /home-now's tier ladder, not here.

// §3 — has the patient submitted this ISO week's weekly check-in?
//   not_submitted → show the check-in CTA (client gates it to Fri–Sun)
//   submitted     → show the confirmation + "awaiting feedback" state
export const weeklyCheckinStatusSchema = z.enum(["not_submitted", "submitted"]);
export type WeeklyCheckinStatus = z.infer<typeof weeklyCheckinStatusSchema>;

// The weekly theme. Copy is LOCALE-RESOLVED server-side (plain strings, like
// /home-now + /today) so the app renders it directly. `key` is the phase it
// was derived from — exposed so the client can vary iconography/analytics
// without re-deriving. `forYouToday` is the §5 support text on the same theme;
// its optional CTA (ctaPath, a carebyella:// deep link or null) deep-dives into
// content.
// TES-401 (the source-of-truth diff) — provenance for the copy the §1/§5
// blocks actually SHOW. Every veckotema carries where its copy came from so QA
// can tell imported Drive content from app-bundled fallback at a glance:
//   daily_tip           imported DT content (Drive), key `dt_NN`
//   weekly_theme        authored/imported weekly theme (admin), key
//                       `weekly_theme_<phase>`
//   app_fallback        the hardcoded THEMES table below, key `fallback_<phase>`
//                       — NOT in Drive; renders only when no DT/weekly theme
//                       covers the phase. Also `fallback_transfer_intro`: the
//                       09 Surface Rules §5 module served between F-01 and
//                       review for prescription-transfer patients (TES-469),
//                       applied over every overlay because only §5 changes.
//   app_safety_fallback the hardcoded SAFETY_PAUSE copy, key
//                       `fallback_safety_pause`
//   package             CP package copy (replace / safety-with-copy), key
//                       `package_<flowKey>`
// `overlay` records a CP overlay applied on top (for `contextualize` the base
// copy still shows, so type/key stay the base's). `updatedAt` is the DB row's
// last-write stamp for DB-backed content, null for app-bundled copy.
export const veckotemaSourceSchema = z.object({
  type: z.enum([
    "daily_tip",
    "weekly_theme",
    "app_fallback",
    "app_safety_fallback",
    "package",
  ]),
  key: z.string(),
  updatedAt: z.string().nullable(),
  fallback: z.boolean(),
  overlay: z
    .object({
      kind: z.enum(["safety", "replace", "contextualize", "for_you_only"]),
      flowKey: z.string().nullable(),
    })
    .nullable(),
});
export type VeckotemaSource = z.infer<typeof veckotemaSourceSchema>;

export const veckotemaSchema = z.object({
  key: treatmentPhaseSchema,
  title: z.string(),
  body: z.string(),
  // TES-587 — a second greeting line under the headline (CP-14's
  // "Hälsningstext"). Absent for every other theme.
  greetingBody: z.string().nullable().optional(),
  // TES-587 — null is an EXPLICIT empty state
  // (CP-14: "Ingen modul visas i detta läge"), not missing copy. The app hides
  // §5 entirely.
  forYouToday: z
    .object({
      title: z.string(),
      body: z.string(),
      ctaPath: z.string().nullable(),
      // Handoff 2026-08-24: curated CTA label from packageTheme
      // (forYouCtaLabel). Optional/null → the client's generic "Läs mer".
      ctaLabel: z.string().nullable().optional(),
      // TES-1496 — a daily tip's article buttons (at most two), each with its
      // own label, in order; a button whose article is unpublished or says
      // no more than the card is left out. Present only for a DT authored
      // with links and a build at or after
      // CLIENT_CONTRACT_CHANGES.dailyTipLinks; such a build renders these
      // instead of ctaPath. ctaPath stays the first link for older builds.
      links: z
        .array(z.object({ label: z.string(), ctaPath: z.string() }))
        .max(2)
        .optional(),
      // TES-1541 (Ö-830 (c)) — the DT's optional "Reflektera" action: the
      // key of the dt.* deepening whose authored reflection question it
      // opens, so the answer keeps the question's identity in Dagbok. Present
      // only for a DT whose deepening has one and a build at or after
      // CLIENT_CONTRACT_CHANGES.dtReflection.
      reflection: z.object({ articleKey: z.string() }).optional(),
    })
    .nullable(),
  // Optional so pre-TES-401 API responses stay valid for older clients.
  source: veckotemaSourceSchema.optional(),
});
export type Veckotema = z.infer<typeof veckotemaSchema>;

// GET /patient-api/v1/home-summary — §1 + §3 + §5 in one call.
//
// `firstName` is nullable BY DESIGN: the Patient row stores no plaintext given
// name (GDPR-minimised — the BankID `givenName` is nulled after JWT issuance,
// see BankIdSession), so there is no reliable source today. The greeting falls
// back to a name-less variant when null. Persisting a name is a separate,
// product-sign-off-gated change (mirrors the coach-burst deferral in
// PatientController.me).
export const homeSummarySchema = z.object({
  firstName: z.string().nullable(),
  veckotema: veckotemaSchema,
  weeklyCheckin: z.object({
    status: weeklyCheckinStatusSchema,
    // TES-1159 — whether HER weekly check-in window is open right now
    // (anchored to her first dose, weekly-checkin-eligibility.ts) and asks
    // for F-04 (false in the window where F-05 replaces it). `status` is
    // about this same window. App 2.0.7 shows the post-submit waiting card
    // only while this is true, instead of its own Friday-to-Sunday clock.
    // Optional so an older server reads as "unknown" and an older app
    // ignores the field.
    windowOpen: z.boolean().optional(),
  }),
  // TES-1392 — the 2.1 compact Hem top: onboarding progress, active
  // treatment or neutral (home-top.ts). Optional and additive: app 2.0.9
  // ignores it, an older server simply omits it.
  homeTop: homeTopSchema.optional(),
});
export type HomeSummary = z.infer<typeof homeSummarySchema>;

// --- veckotema derivation ---------------------------------------------------
// phase → bilingual theme copy. Pure DATA, keyed on the DERIVED treatmentPhase
// (deriveTreatmentPhase in patient-journey-state.ts). Swedish is the approved
// base; English is derived from it. The copy is intentionally warm + short:
// `body` is the greeting sub-line (§1), `forYouToday` is the support text (§5)
// on the same theme so the two never diverge.
const THEMES: Record<
  TreatmentPhase,
  {
    title: Record<Locale, string>;
    body: Record<Locale, string>;
    forYouToday: {
      title: Record<Locale, string>;
      body: Record<Locale, string>;
    };
  }
> = {
  pre_treatment: {
    title: { sv: "Förberedelse", en: "Getting ready" },
    body: {
      sv: "Vi förbereder din start – ett steg i taget.",
      en: "We're getting you ready to start — one step at a time.",
    },
    forYouToday: {
      title: { sv: "Innan du börjar", en: "Before you begin" },
      body: {
        sv: "Bekanta dig med resan framåt så känns första steget tryggare.",
        en: "Get familiar with the journey ahead so the first step feels safe.",
      },
    },
  },
  start_phase: {
    title: { sv: "Mjuk start", en: "A gentle start" },
    body: {
      sv: "Den här veckan fokuserar vi på en mjuk start med kroppens signaler i fokus.",
      en: "This week we focus on a gentle start, tuned to your body's signals.",
    },
    forYouToday: {
      title: { sv: "Måltider som fungerar", en: "Meals that work" },
      body: {
        sv: "Aptit och mättnad förändras i början. Näringsrika måltider i mindre portioner blir lättare att få till.",
        en: "Appetite and fullness shift early on. Smaller, nutritious meals are easier to manage.",
      },
    },
  },
  dose_adjustment_phase: {
    title: { sv: "Hitta rätt dos", en: "Finding the right dose" },
    body: {
      sv: "Den här veckan fokuserar vi på måltider som fungerar när aptiten är låg.",
      en: "This week we focus on meals that work when your appetite is low.",
    },
    forYouToday: {
      title: {
        sv: "Näring när aptiten är låg",
        en: "Nutrition when appetite is low",
      },
      body: {
        sv: "När aptiten är låg kan näringsrika måltider vara lättare att få till i små, täta portioner.",
        en: "When appetite is low, nutritious meals in small, frequent portions can be easier to get in.",
      },
    },
  },
  active_weight_loss: {
    title: { sv: "Aktiv viktnedgång", en: "Active weight loss" },
    body: {
      sv: "Den här veckan bygger vi vidare på vanor som håller i sig.",
      en: "This week we build on habits that last.",
    },
    forYouToday: {
      title: { sv: "Protein och rörelse", en: "Protein and movement" },
      body: {
        sv: "Tillräckligt med protein och daglig rörelse hjälper dig att behålla muskler medan vikten går ner.",
        en: "Enough protein and daily movement help you keep muscle while your weight goes down.",
      },
    },
  },
  stabilisation: {
    title: { sv: "Stabilisering", en: "Stabilising" },
    body: {
      sv: "Den här veckan låter vi de nya vanorna sätta sig.",
      en: "This week we let your new habits settle in.",
    },
    forYouToday: {
      title: { sv: "Vanor som håller", en: "Habits that hold" },
      body: {
        sv: "Att befästa rutinerna nu gör det lättare att behålla resultatet framåt.",
        en: "Locking in your routine now makes it easier to keep your results going forward.",
      },
    },
  },
  maintenance: {
    title: { sv: "Underhåll", en: "Maintenance" },
    body: {
      sv: "Den här veckan handlar om att behålla det du har uppnått.",
      en: "This week is about holding on to what you've achieved.",
    },
    forYouToday: {
      title: { sv: "Behåll dina framsteg", en: "Keep your progress" },
      body: {
        sv: "Fortsätt med det som fungerar. Små justeringar räcker för att hålla kvar resultatet.",
        en: "Keep doing what works. Small adjustments are enough to hold your results.",
      },
    },
  },
  paused: {
    title: { sv: "Paus", en: "Paused" },
    body: {
      sv: "Din behandling är pausad just nu. Vi finns här när du är redo igen.",
      en: "Your treatment is paused for now. We're here when you're ready again.",
    },
    forYouToday: {
      title: { sv: "Under pausen", en: "During your pause" },
      body: {
        sv: "Även under en paus gör dina vardagsvanor skillnad. Fortsätt i din takt.",
        en: "Even during a pause, your everyday habits matter. Keep going at your own pace.",
      },
    },
  },
  ended: {
    title: { sv: "Avslutad", en: "Completed" },
    body: {
      sv: "Din behandling är avslutad. Vi är stolta över resan du har gjort.",
      en: "Your treatment has ended. We're proud of the journey you've made.",
    },
    forYouToday: {
      title: { sv: "Vägen framåt", en: "The road ahead" },
      body: {
        sv: "De vanor du byggt är dina att behålla. Hör av dig om du vill komma tillbaka.",
        en: "The habits you've built are yours to keep. Reach out if you'd like to return.",
      },
    },
  },
};

// Resolve the weekly theme for a derived phase in the patient's locale. Pure +
// deterministic — the API calls this in /home-summary and tests assert the
// §1/§5 shared-focus invariant off it. §5's CTA is optional (frivillig, spec
// page 5): null for now, wired to a content deep-dive when the content surface
// lands.
export function veckotemaForPhase(
  phase: TreatmentPhase,
  locale: Locale,
): Veckotema {
  const theme = THEMES[phase];
  return {
    key: phase,
    // title/body are schema-optional (override_for_you_only never carries
    // them) but authoring refinement guarantees them on every replace-capable
    // behavior; the fallback is corrupt-snapshot armor, never a render path.
    title: theme.title?.[locale] ?? "",
    body: theme.body?.[locale] ?? "",
    forYouToday: {
      title: theme.forYouToday.title[locale],
      body: theme.forYouToday.body[locale],
      ctaPath: null,
    },
    // TES-401: this table is app-bundled copy with no Drive/import source —
    // flagged so QA never mistakes it for imported content.
    source: {
      type: "app_fallback",
      key: `fallback_${phase}`,
      updatedAt: null,
      fallback: true,
      overlay: null,
    },
  };
}

// --- TES-372: package theme overlays ----------------------------------------

// The built-in safety-pause copy: an active `override_for_safety` package with
// no packageTheme of its own falls back to this — calm, no tasks, points at
// the care team. The spec's "only medical safety pauses the theme". `key`
// stays the derived phase (client iconography/analytics keep working).
const SAFETY_PAUSE = {
  title: {
    sv: "Vi tar det lugnt just nu",
    en: "We're taking it easy right now",
  },
  body: {
    sv: "Din läkare har pausat veckans tema av medicinska skäl. Följ råden du fått från vårdteamet.",
    en: "Your doctor has paused this week's theme for medical reasons. Follow the guidance from your care team.",
  },
  forYouToday: {
    title: { sv: "Just nu gäller", en: "For now" },
    body: {
      sv: "Vila, följ din plan och hör av dig i chatten om något känns fel.",
      en: "Rest, follow your plan, and reach out in the chat if something feels wrong.",
    },
  },
} as const;

export function safetyPauseVeckotema(
  phase: TreatmentPhase,
  locale: Locale,
): Veckotema {
  return {
    key: phase,
    title: SAFETY_PAUSE.title[locale],
    body: SAFETY_PAUSE.body[locale],
    forYouToday: {
      title: SAFETY_PAUSE.forYouToday.title[locale],
      body: SAFETY_PAUSE.forYouToday.body[locale],
      ctaPath: null,
    },
  };
}

// set_journey_context / start_new_weekly_theme (and a safety override that
// brings its own copy): the package's bilingual theme copy, locale-resolved
// into the same Veckotema shape the base themes use. `key` stays the derived
// phase — the overlay swaps COPY, not the phase identity.
export function packageThemeToVeckotema(
  theme: PackageTheme,
  phase: TreatmentPhase,
  locale: Locale,
): Veckotema {
  return {
    key: phase,
    // title/body are schema-optional (override_for_you_only never carries
    // them) but authoring refinement guarantees them on every replace-capable
    // behavior; the fallback is corrupt-snapshot armor, never a render path.
    title: theme.title?.[locale] ?? "",
    body: theme.body?.[locale] ?? "",
    // TES-887 — no §5 copy means NO §5, not an empty card. A package may
    // replace §1 and deliberately render no "För dig idag" surface at all;
    // CP-60/61 canon requires exactly that ("får inte märkas 'För dig idag'",
    // "visar ingen redaktionell yta märkt För dig idag"). null is the shape
    // the app already treats as an explicit empty state (TES-587/CP-14) — it
    // hides the section rather than drawing a blank one. refinePackageHeader
    // enforces the pair, so one half alone cannot reach this.
    forYouToday:
      theme.forYouTitle || theme.forYouBody
        ? {
            title: theme.forYouTitle?.[locale] ?? "",
            body: theme.forYouBody?.[locale] ?? "",
            // v15 — the package theme may steer the §5 "Läs mer" link (curated
            // CP copy skips the TES-391 says-more gate on purpose).
            ctaPath: theme.forYouArticleKey
              ? `carebyella://article/${theme.forYouArticleKey}`
              : null,
            ctaLabel: theme.forYouCtaLabel?.[locale] ?? null,
          }
        : null,
  };
}

// override_for_you_only (QA-baseline 2026-08-19): the base theme survives in
// full (§1 "Tema" + "Hälsning" untouched); ONLY "För dig idag" (§5) is replaced
// with the package's forYou copy. Pure so the drift-guard specs can assert
// title/body identity with the base.
export function overrideForYouToday(
  base: Veckotema,
  theme: PackageTheme,
  locale: Locale,
): Veckotema {
  return {
    ...base,
    forYouToday: {
      // TES-873 — see packageThemeToVeckotema above.
      title: theme.forYouTitle?.[locale] ?? "",
      body: theme.forYouBody?.[locale] ?? "",
      ctaPath: theme.forYouArticleKey
        ? `carebyella://article/${theme.forYouArticleKey}`
        : null,
      ctaLabel: theme.forYouCtaLabel?.[locale] ?? null,
    },
  };
}

// contextualize_weekly: the base theme survives; the package's `theme` label
// is appended as one context line on "För dig idag" (ASSUMPTION per TES-372 —
// cheap, revisit against Drive). Pure so the §1/§5 shared-focus invariant is
// testable off it.
export function contextualizeVeckotema(
  base: Veckotema,
  themeLabel: string,
  locale: Locale,
): Veckotema {
  const line =
    locale === "en" ? `Right now: ${themeLabel}.` : `Just nu: ${themeLabel}.`;
  if (!base.forYouToday) return base;
  return {
    ...base,
    forYouToday: {
      ...base.forYouToday,
      body: `${base.forYouToday.body}\n\n${line}`,
    },
  };
}
